import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';

interface WorkloadCost {
  workload: string;
  namespace: string;
  rawCost: number;
  env: 'dev' | 'prd';
}

interface EntityCostRow {
  entityId: string;
  rawCost: number;
  dept: string | null;
  environments: string[];
  workloads: WorkloadCost[];
}

interface CatalogEntityInsights {
  windowDays: number;
  total: number;
  entities: EntityCostRow[];
}

function formatKRW(v: number): string {
  return `₩${v.toLocaleString('ko-KR')}`;
}

export const EntityCostContent = () => {
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const [data, setData] = React.useState<CatalogEntityInsights | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [tick, setTick] = React.useState(0);

  const entityName = entity?.metadata?.name || '';

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetchApi.fetch('/api/gcp-cost/insights/by-catalog-entity');
        if (!res.ok) throw new Error(await res.text());
        setData(await res.json());
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchApi, tick]);

  if (loading) {
    return (
      <Box p={3} display="flex" alignItems="center" style={{ gap: 12 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">비용 데이터 로딩 중…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="warning">비용 조회 실패: {error}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  const row = data.entities.find(e => e.entityId === entityName);

  if (!row) {
    return (
      <Box p={3}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              이 entity 에 대한 비용 데이터 없음
            </Typography>
            <Typography variant="body2" color="textSecondary">
              최근 {data.windowDays}일 기준 GCP billing export 에서 해당 entity
              (<code>{entityName}</code>) 와 연관된 워크로드가 발견되지 않았습니다.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              가능한 원인:
            </Typography>
            <ul style={{ marginTop: 4, color: '#666' }}>
              <li>아직 배포되지 않음 (첫 배포 후 24h 경과 필요)</li>
              <li>
                <code>backstage.io/kubernetes-id</code> 어노테이션이 catalog-info.yaml 에 없음
              </li>
              <li>
                비용 기여가 일 평균 ₩100 미만 (집계 하한 필터)
              </li>
            </ul>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const devWorkloads = row.workloads.filter(w => w.env === 'dev');
  const prdWorkloads = row.workloads.filter(w => w.env === 'prd');
  const devTotal = devWorkloads.reduce((s, w) => s + w.rawCost, 0);
  const prdTotal = prdWorkloads.reduce((s, w) => s + w.rawCost, 0);
  const devRatio = row.rawCost > 0 ? Math.round((devTotal / row.rawCost) * 100) : 0;

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          Entity 비용 상세 — 최근 {data.windowDays}일
        </Typography>
        <IconButton size="small" onClick={() => setTick(t => t + 1)} title="새로고침">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <Card variant="outlined" style={{ marginBottom: 12 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 12 }}>
            <Box flex={1} minWidth={200}>
              <Typography variant="caption" color="textSecondary">
                총 비용
              </Typography>
              <Typography variant="h5" style={{ fontWeight: 700 }}>
                {formatKRW(row.rawCost)}
              </Typography>
              {row.dept && (
                <Chip
                  label={`dept: ${row.dept}`}
                  size="small"
                  style={{ marginTop: 4, backgroundColor: '#0097a7', color: '#fff' }}
                />
              )}
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" color="textSecondary">
                Prd vs Dev
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {formatKRW(prdTotal)} / {formatKRW(devTotal)}
              </Typography>
              <Typography
                variant="caption"
                style={{
                  color: devRatio > 50 ? '#f57c00' : '#666',
                }}
              >
                Dev 비율 {devRatio}% {devRatio > 50 && '⚠️ Dev 과다'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            워크로드별 내역 ({row.workloads.length}건)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workload</TableCell>
                  <TableCell>Namespace</TableCell>
                  <TableCell>Env</TableCell>
                  <TableCell align="right">비용</TableCell>
                  <TableCell align="right">비율</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {row.workloads
                  .slice()
                  .sort((a, b) => b.rawCost - a.rawCost)
                  .map((w, i) => {
                    const pct = row.rawCost > 0
                      ? ((w.rawCost / row.rawCost) * 100).toFixed(1)
                      : '0';
                    return (
                      <TableRow key={`${w.namespace}-${w.workload}-${i}`}>
                        <TableCell style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                          <Tooltip title={w.workload}>
                            <span>{w.workload}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell style={{ fontSize: '0.82rem' }}>
                          {w.namespace}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={w.env}
                            size="small"
                            style={{
                              backgroundColor: w.env === 'prd' ? '#d32f2f' : '#1976d2',
                              color: '#fff',
                              minWidth: 42,
                              fontSize: '0.7rem',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatKRW(Math.round(w.rawCost))}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="textSecondary">
                            {pct}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Typography
        variant="caption"
        color="textSecondary"
        style={{ display: 'block', marginTop: 12 }}
      >
        원본 데이터: BigQuery billing_export · 집계: <code>/api/gcp-cost/insights/by-catalog-entity</code>
        · 최근 {data.windowDays}일 raw cost 합산 · 일 평균 ₩100 미만 워크로드 제외
      </Typography>
    </Box>
  );
};
